import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  AlertTriangle,
  BarChart3,
  Target,
  GitBranch,
  TrendingUp,
  Shield,
  ArrowRight,
  Activity,
  Flag,
  Layers,
} from "lucide-react";

const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  purple: "#6C63FF",
  lime: "#a8e063",
  amber: "#f59e0b",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  glassBg: "rgba(11,13,40,0.7)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

// ─── Hero Snapshot ─────────────────────────────────────────────────────────────
const RiskHeroSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <BarChart3 size={14} style={{ color: C.amber }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Risk Management</span>
      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(248,113,113,0.15)", color: "#f87171" }}>4 High Risk</span>
    </div>
    <div className="p-3 grid grid-cols-3 gap-2 mb-3">
      {[
        { label: "Total Risks", value: "14", color: C.purple },
        { label: "High Risks", value: "4", color: "#f87171" },
        { label: "Gaps", value: "3", color: C.amber },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-2 text-center" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
          <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
          <div className="text-[9px]" style={{ color: C.dimSlate }}>{s.label}</div>
        </div>
      ))}
    </div>
    {/* Heat map */}
    <div className="px-3 pb-3">
      <div className="text-[10px] mb-1.5 font-semibold" style={{ color: C.dimSlate }}>Risk Heat Map</div>
      <div className="grid gap-0.5 mb-2" style={{ gridTemplateColumns: "repeat(5,1fr)" }}>
        {Array.from({ length: 25 }, (_, i) => {
          const row = Math.floor(i / 5);
          const col = i % 5;
          const score = (row + 1) * (col + 1);
          const filled = [3, 7, 11, 16, 19, 21, 23].includes(i);
          const bg = score >= 12 ? "rgba(248,113,113,0.4)" : score >= 8 ? "rgba(245,158,11,0.4)" : "rgba(168,224,99,0.3)";
          return (
            <div key={i} className="rounded-sm flex items-center justify-center aspect-square text-[7px] font-bold"
              style={{ background: filled ? bg : `${bg.replace("0.4", "0.1").replace("0.3", "0.07")}`, color: filled ? C.white : "transparent", border: filled ? `1px solid ${bg.replace("0.4", "0.6").replace("0.3", "0.5")}` : "none" }}>
              {filled ? "●" : ""}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mb-3">
        {([["Low", "rgba(168,224,99,0.4)"], ["Moderate", "rgba(245,158,11,0.4)"], ["Critical", "rgba(248,113,113,0.4)"]] as [string, string][]).map(([l, c]) => (
          <div key={l} className="flex items-center gap-1 text-[9px]" style={{ color: C.dimSlate }}>
            <div className="w-2 h-2 rounded-sm" style={{ background: c }} />{l}
          </div>
        ))}
      </div>
      <div className="text-[10px] mb-1.5 font-semibold" style={{ color: C.dimSlate }}>Top Risks</div>
      {[
        { name: "Data Breach Risk", severity: "Critical", color: "#f87171" },
        { name: "Regulatory Non-Compliance", severity: "High", color: C.amber },
        { name: "Operational Disruption", severity: "Moderate", color: "#a78bfa" },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 py-1">
          <AlertTriangle size={10} style={{ color: r.color }} />
          <span className="text-[10px] flex-1" style={{ color: C.slate }}>{r.name}</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${r.color}18`, color: r.color }}>{r.severity}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Feature Cards ─────────────────────────────────────────────────────────────
const features = [
  { icon: <Target size={20} />, color: C.amber, title: "Risk Register", desc: "Centralise all organisational risks with owner assignment, category classification, and 5×5 scoring." },
  { icon: <Activity size={20} />, color: "#f87171", title: "Heat Map Visualisation", desc: "See your entire risk landscape plotted by likelihood and impact in an interactive 5×5 heat map." },
  { icon: <BarChart3 size={20} />, color: C.purple, title: "Risk Scoring Matrix", desc: "Quantify each risk with standardised likelihood and impact scores. Consistent, objective, defensible." },
  { icon: <GitBranch size={20} />, color: C.lime, title: "Policy Traceability", desc: "Link every risk directly to its mitigating policy. Know which risks are covered and which are exposed." },
  { icon: <Flag size={20} />, color: "#a78bfa", title: "Risk Appetite Thresholds", desc: "Define tolerance levels and appetite statements across risk categories for your organisation." },
  { icon: <Layers size={20} />, color: C.amber, title: "Three Lines of Defence", desc: "Align your risk governance to the 3LoD framework — accountability at every level." },
];

const steps = [
  { num: "01", title: "Identify", desc: "Log risks from any category — operational, financial, compliance, or strategic — into the central register." },
  { num: "02", title: "Score", desc: "Rate each risk on a 5×5 likelihood-impact matrix. Heat map updates automatically." },
  { num: "03", title: "Monitor", desc: "Track risk trends over time, assign owners, and link mitigating policies to close the loop." },
];

const stats = [
  { value: "5×5", label: "Scoring matrix" },
  { value: "14+", label: "Risk categories" },
  { value: "100%", label: "Policy traceability" },
  { value: "3LoD", label: "Framework aligned" },
];

export default function RiskFeature() {
  return (
    <div style={{ background: C.navy, minHeight: "100vh", fontFamily: "'DM Sans', ui-sans-serif, sans-serif", color: C.white }}>
      <MarketingNav />

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-32 pb-24 px-6 md:px-16 lg:px-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 50% at 20% 40%, rgba(245,158,11,0.06) 0%, transparent 70%)` }} />

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
              style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.25)", color: C.amber }}>
              <AlertTriangle size={13} /> Risk Management Module
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="text-4xl md:text-5xl font-bold leading-[1.15] mb-5"
              style={{ letterSpacing: "-0.03em" }}>
              See Every Risk.<br />
              <span style={{ color: C.amber }}>Act Before It Lands.</span>
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="text-base mb-8 max-w-md leading-relaxed" style={{ color: C.slate }}>
              Identify, score, and track organisational risks across categories. Visualise your entire risk landscape with a live heat map — and link every risk directly to mitigating policies.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="flex flex-wrap gap-3">
              <NavLink to="/auth"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: C.amber, color: C.navy }}>
                Start Free Trial <ArrowRight size={15} />
              </NavLink>
              <NavLink to="/consulting"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ border: `1px solid ${C.border}`, color: C.slate }}>
                Request a Demo
              </NavLink>
            </motion.div>
          </div>

          {/* Right — mock UI */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2}>
            <RiskHeroSnapshot />
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6 md:px-16 lg:px-24" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: C.amber }}>{s.value}</div>
              <div className="text-sm" style={{ color: C.dimSlate }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Key Features ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>
              A complete risk management toolkit
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: C.slate }}>
              From identification to appetite — everything you need to govern risk across your organisation.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
                className="rounded-xl p-6"
                style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}` }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${f.color}16`, color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ color: C.white }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24" style={{ background: "rgba(255,255,255,0.015)" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>How it works</h2>
            <p className="text-base" style={{ color: C.slate }}>Three steps from risk identification to active monitoring.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="relative">
                <div className="text-6xl font-black mb-4 leading-none" style={{ color: "rgba(245,158,11,0.1)" }}>{s.num}</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: C.white }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 text-2xl" style={{ color: C.border }}>→</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center rounded-2xl px-8 py-16"
          style={{ background: `linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(108,99,255,0.06) 100%)`, border: `1px solid ${C.border}` }}>
          <AlertTriangle size={36} className="mx-auto mb-5" style={{ color: C.amber }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ letterSpacing: "-0.025em" }}>
            Stop discovering risks too late.
          </h2>
          <p className="text-base mb-8 max-w-md mx-auto" style={{ color: C.slate }}>
            Get full visibility over your risk landscape before auditors — or incidents — do it for you.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <NavLink to="/auth"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: C.amber, color: C.navy }}>
              Start Free Trial <ArrowRight size={15} />
            </NavLink>
            <NavLink to="/consulting"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all hover:opacity-80"
              style={{ border: `1px solid ${C.border}`, color: C.slate }}>
              Request a Demo
            </NavLink>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
